import { createContext } from "react";

import { isSameDay } from "date-fns";
import { makeAutoObservable, runInAction } from "mobx";

import client from "@/client";
import { graphql } from "@/gql/gql";
import {
  CreateWorkScheduleInput,
  RuleKind,
  UpdateWorkScheduleInput,
} from "@/gql/graphql";

import Interval from "./Interval";
import Rule from "./Rule";

export default class Schedule {
  id = "";
  wdayRules: Rule[] = [
    Rule.wday(this, "Sunday"),
    Rule.wday(this, "Monday"),
    Rule.wday(this, "Tuesday"),
    Rule.wday(this, "Wednesday"),
    Rule.wday(this, "Thursday"),
    Rule.wday(this, "Friday"),
    Rule.wday(this, "Saturday"),
  ];
  dateRules: Rule[] = [];
  submitMode = "";

  get isFormValid(): boolean {
    return (
      this.wdayRules.every((wday) => wday.isValid) &&
      this.dateRules.every((dateRules) => dateRules.isValid)
    );
  }

  get mode(): string {
    return this.submitMode;
  }

  constructor() {
    makeAutoObservable(this);
  }

  load = async () => {
    const { data } = await client.query({
      query: scheduleQuery,
    });

    runInAction(() => {
      if (!data.coachViewer.workSchedule) return;

      const rules = data?.coachViewer.workSchedule?.rules ?? [];
      this.id = data.coachViewer.workSchedule?.id ?? "";
      this.wdayRules = rules
        .filter((rule) => rule.ruleType === RuleKind.Wday)
        .map((rul) => Rule.fromFragment(this, rul));
      this.dateRules = rules
        .filter((rule) => rule.ruleType === RuleKind.Date)
        .map((rul) => Rule.fromFragment(this, rul));
    });
  };

  findOverrideRule(date: Date | null | undefined) {
    if (date == null) return undefined;

    return this.dateRules.find((r) => r.date && isSameDay(r.date, date));
  }

  saveOverrideRule = (rule: Rule) => {
    if (rule.ruleType !== RuleKind.Date) return;

    // If the date already exists, we'll replace the intervals of the existing
    // rule instead.
    const existing = this.findOverrideRule(rule.date);
    if (existing) {
      // // Mark for destroy all existing intervals and push new intervals.
      existing.removeIntervals();
      existing.intervals.push(...rule.intervals);
    } else {
      this.dateRules.push(rule);
    }
  };

  removeOverrideRule(rule: Rule) {
    this.dateRules.splice(this.dateRules.indexOf(rule), 1);
  }

  copyIntervals = (from: Rule, recipients: Rule[]) => {
    recipients.forEach((recipient) => {
      recipient.intervals = from.intervals.map((interval) => {
        const newInterval = new Interval();
        newInterval.startTime = interval.startTime;
        newInterval.endTime = interval.endTime;
        newInterval._destroy = interval._destroy;
        return newInterval;
      });
    });
  };

  get combinedRules(): Rule[] {
    return [...this.wdayRules, ...this.dateRules];
  }

  toCreateWorkScheduleInput(coachId: string): CreateWorkScheduleInput {
    const combinedRules = this.combinedRules;
    const createWorkScheduleInput: CreateWorkScheduleInput = {
      attributes: {
        coachId: coachId,
        rulesAttributes: combinedRules?.map((rule: Rule) => {
          return {
            ...rule.toAttributes(),
            intervalsAttributes: rule.intervals?.map((interval: Interval) => {
              return interval.toAttributes();
            }),
          };
        }),
      },
    };

    return createWorkScheduleInput;
  }

  toUpdateWorkScheduleInput(): UpdateWorkScheduleInput {
    const combinedRules = this.combinedRules;
    const updateWorkScheduleInput: UpdateWorkScheduleInput = {
      id: this.id,
      patch: {
        rulesAttributes: combinedRules?.map((rule) => {
          return {
            ...rule.toAttributes(),
            intervalsAttributes: rule.intervals?.map((interval) => {
              return interval.toAttributes();
            }),
          };
        }),
      },
    };

    return updateWorkScheduleInput;
  }
}

const scheduleQuery = graphql(`
  query ScheduleQuery {
    coachViewer {
      id
      workSchedule {
        ...CoachWorkSchedule
      }
    }
  }

  fragment CoachWorkSchedule on WorkSchedule {
    id
    coachId
    rules {
      ...Rule
    }
  }

  fragment Rule on Rule {
    id
    date
    ruleType
    wday
    intervals {
      ...Interval
    }
  }

  fragment Interval on Interval {
    id
    ruleId
    startTime
    endTime
  }
`);

export const ScheduleContext = createContext<Schedule | undefined>(undefined);
